import { Injectable } from '@angular/core';
import { FuseNavigationItem, FuseNavigationService } from '@fuse/components/navigation';
import { BadisOauthLoginService } from './auth/badis-oauth-login.service';
import { CustomEventService } from './custom-event.service';
import { DataService } from './data.service';
import { GlobalVariablesService } from './global-variables.service';
import { Observable, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { CustomReportService } from '../../shared/components/custom-report-editor/custom-report.service'; // Add this import
import { ServiceName, ServiceReadyStateService } from './service-ready-state.service';
import { CustomReportEditor } from '@app/shared/components/custom-report-editor/models/custom-report-editor.model';
import { StyleManagerService } from './style-manager-service.service';
import { Guid } from '../constants/constants';
import { UtilityService } from '@app/modules/live/services/utility.service';
import { FuseLoadingService } from '@fuse/services/loading';
import { NavigationService } from '@app/core/navigation/navigation.service';
import { AppPermissionService } from './app-permission.service';
import { ThirdPartyAppsFeatureService } from './third-party-apps-feature.service';
import { SettingsService } from './settings/settings.service';

@Injectable()
export class SystemMenuService {

    private menuTemplate: FuseNavigationItem[];
    private newNavigation: FuseNavigationItem[];
    private customReportsRefreshVersion = 0;
    public customReports: CustomReportEditor.CustomReport[] = []; // Custom reports array

    constructor(
        private _fuseNavigationService: FuseNavigationService,
        private _dataService: DataService,
        private _badisOauthLoginService: BadisOauthLoginService,
        private customEventService: CustomEventService,
        private varService: GlobalVariablesService,
        private customReportService: CustomReportService,
        private readyStateService: ServiceReadyStateService,
        private styleManager: StyleManagerService,
        private utility: UtilityService,
        private loadingService: FuseLoadingService,
        private navigationService: NavigationService,
        private permissionService: AppPermissionService,
        private thirdPartyAppsFeature: ThirdPartyAppsFeatureService,
        private settingsService: SettingsService) {
        this.customEventService.on('object-types-updated').subscribe(() => this.reloadSystemMenu());
        this.customEventService.on('third-party-apps-updated').subscribe(() => this.reloadSystemMenu());
        this.settingsService.settingChanged.subscribe(setting => {
            if (setting?.primaryKey === 'ThirdPartyApps.Enabled') {
                this.reloadSystemMenu();
            }
        });
        this.settingsService.isReady.subscribe(isReady => {
            if (isReady === true) {
                this.reloadSystemMenu();
            }
        });
    }

    public populateSystemMenu(): void {

        const newSystemMenu: any = this.varService.navizardSystemMenuSource || this.varService.navizardSystemMenu;

        if (newSystemMenu === null || newSystemMenu === undefined) { return; }

        this.menuTemplate = this.cloneNavigation(newSystemMenu);
        this.newNavigation = this.cloneNavigation(this.menuTemplate);

        this.permissionService.loadCurrent(true).subscribe(() => {
            this.newNavigation = this.cloneNavigation(this.menuTemplate);
            this.reloadSystemMenu();
            this.emitNavigationUpdate();

            this.readyStateService.setServiceReady(ServiceName.Menu, true);
        });
    }

    protected reloadSystemMenu(): void {
        if (!this.newNavigation) {
            return;
        }

        this.applyCustomReportsMenu(this.customReports || []);
        this.refreshCustomReportsMenu().subscribe();
        this.refreshInstalledAppsMenu().subscribe();
    }

    //get custom report from id
    public getCustomReportById(id: string): CustomReportEditor.CustomReport {
        return this.customReports.find((report) => report.id.toString() === id);
    }

    // Refactored metod: Kullanıcıya özel raporları çekmek için
    private getUserCustomReports(): Observable<CustomReportEditor.CustomReport[]> {
        return this.customReportService.getUserCustomReports();
    }

    // Custom reports menu refresh function
    public refreshCustomReportsMenu(): Observable<CustomReportEditor.CustomReport[]> {
        const refreshVersion = ++this.customReportsRefreshVersion;
        this.loadingService.setAutoMode(false);

        return this.getUserCustomReports().pipe(
            catchError((error) => {
                console.error('Error loading custom reports menu:', error);
                return of(this.customReports || []);
            }),
            tap((reports: CustomReportEditor.CustomReport[]) => {
                if (refreshVersion !== this.customReportsRefreshVersion) {
                    return;
                }

                this.customReports = reports || [];
                this.applyCustomReportsMenu(this.customReports);
            }),
            finalize(() => {
                this.loadingService.setAutoMode(true);
            })
        );
    }

    private applyCustomReportsMenu(reports: CustomReportEditor.CustomReport[]): void {
        if (!this.newNavigation) {
            return;
        }

        const reportsMenu = this._fuseNavigationService.getItem('reports', this.newNavigation);
        if (!reportsMenu) {
            return;
        }

        reportsMenu.children = reportsMenu.children?.filter(child => child.id !== 'custom-reports') || [];

        const customReportsMenu: FuseNavigationItem = {
            id: 'custom-reports',
            title: 'SystemAdmin.Menu.Reports.CustomReports.Title',
            icon: 'fal:file-chart-column',
            type: 'collapsable',
            roles: ['SuperAdmin', 'Admin', 'User', 'Reporter'],
            classes: { wrapper: 'text-yellow-500' },
            badge: {
                title: 'Beta',
                classes: 'inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-500 ring-1 ring-inset ring-red-400/20'
            },
            children: (reports || [])
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((report) => this.generateCustomReportMenuItem(report))
        };

        if (!reportsMenu.children) {
            reportsMenu.children = [];
        }

        reportsMenu.children.push(customReportsMenu);
        this.emitNavigationUpdate();
    }

    public refreshInstalledAppsMenu(): Observable<any[]> {
        if (!this.thirdPartyAppsFeature.isEnabled()) {
            if (this.newNavigation) {
                this.removeThirdPartyAppsMenuItems(this.newNavigation);
                this.emitNavigationUpdate();
            }

            return of([]);
        }

        return this._dataService.getV2<any[]>('installed-applications', true, { suppressAccessDeniedRedirect: true }).pipe(
            catchError(() => of([])),
            tap((apps: any[]) => {
                if (!this.newNavigation) {
                    return;
                }

                const enabledApps = (apps || [])
                    .filter(app => this.isInstalledAppEnabled(app))
                    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

                this.removeThirdPartyAppsMenuItems(this.newNavigation);

                const canManageApps = this.permissionService.canViewSync('third-party-app-store') === true;
                if (!enabledApps.length && !canManageApps) {
                    this.emitNavigationUpdate();
                    return;
                }

                const appsMenu: FuseNavigationItem = {
                    id: 'third-party-apps',
                    title: 'Apps',
                    subtitle: 'Navizard app catalog and installed apps',
                    type: 'group',
                    icon: 'fal:grid-2-plus',
                    children: [
                        ...(canManageApps ? [this.generateAppStoreMenuItem()] : []),
                        ...enabledApps.map(app => this.generateInstalledAppMenuItem(app)),
                    ],
                };

                const dashboardsIndex = this.newNavigation.findIndex(item => item.id === 'dashboards');
                if (dashboardsIndex >= 0) {
                    this.newNavigation.splice(dashboardsIndex + 1, 0, appsMenu);
                } else {
                    this.newNavigation.push(appsMenu);
                }

                this.emitNavigationUpdate();
            })
        );
    }

    private generateAppStoreMenuItem(): FuseNavigationItem {
        return {
            id: 'third-party-app-store',
            title: 'App Store',
            subtitle: 'Browse and install Navizard apps',
            icon: 'fal:grid-2-plus',
            link: '/apps',
            exactMatch: true,
            type: 'basic',
        };
    }

    private isInstalledAppEnabled(app: any): boolean {
        const status = String(app?.status ?? '').toLocaleLowerCase();
        return status === 'enabled' || status === '1';
    }

    private generateInstalledAppMenuItem(app: any): FuseNavigationItem {
        const menuItems = (app.menuItems || [])
            .slice()
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        if (menuItems.length > 1) {
            return {
                id: `third-party-app-${app.id}`,
                title: app.name,
                subtitle: app.summary,
                icon: this.resolveInstalledAppMenuIcon(app.iconUrl),
                type: 'collapsable',
                children: menuItems.map((menuItem, index) => ({
                    id: `third-party-app-${app.id}-${menuItem.id ?? index}`,
                    title: menuItem.title || app.name,
                    subtitle: menuItem.subtitle,
                    icon: this.resolveInstalledAppMenuIcon(menuItem.icon || app.iconUrl),
                    link: this.resolveInstalledAppMenuLink(app, menuItem),
                    exactMatch: true,
                    type: 'basic',
                })),
            };
        }

        const primaryMenuItem = menuItems[0];
        return {
            id: `third-party-app-${app.id}`,
            title: primaryMenuItem?.title || app.name,
            subtitle: primaryMenuItem?.subtitle || app.summary,
            icon: this.resolveInstalledAppMenuIcon(primaryMenuItem?.icon || app.iconUrl),
            link: this.resolveInstalledAppMenuLink(app, primaryMenuItem),
            exactMatch: true,
            type: 'basic',
        };
    }

    private resolveInstalledAppMenuIcon(icon?: string): string {
        if (!icon) {
            return 'fal:window';
        }

        const value = String(icon);
        if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
            return 'fal:window';
        }

        return value;
    }

    private resolveInstalledAppMenuLink(app: any, menuItem?: any): string {
        if (menuItem?.route && String(menuItem.route).startsWith('/')) {
            return menuItem.route;
        }

        return `/apps/${app.slug}`;
    }

    private removeMenuItemById(items: FuseNavigationItem[], id: string): boolean {
        let changed = false;

        for (let index = items.length - 1; index >= 0; index--) {
            const item = items[index] as any;

            if (item.id === id) {
                items.splice(index, 1);
                changed = true;
                continue;
            }

            if (item.children?.length) {
                changed = this.removeMenuItemById(item.children, id) || changed;
            }
        }

        return changed;
    }

    private removeThirdPartyAppsMenuItems(items: FuseNavigationItem[]): boolean {
        let changed = false;

        for (let index = items.length - 1; index >= 0; index--) {
            const item = items[index] as any;

            if (this.isThirdPartyAppsMenuItem(item)) {
                items.splice(index, 1);
                changed = true;
                continue;
            }

            if (item.children?.length) {
                changed = this.removeThirdPartyAppsMenuItems(item.children) || changed;
                if (!item.children.length) {
                    item.children = undefined;
                }
            }
        }

        return changed;
    }

    public getMenuItemById(id: string | Guid): FuseNavigationItem | undefined {
        if (!this.newNavigation) {
            console.error('Navigation component or navigation data is not initialized.');
            return undefined;
        }
        return this.findMenuItemById(this.newNavigation, id);
    }

    private findMenuItemById(items: FuseNavigationItem[], id: string | Guid): FuseNavigationItem | undefined {
        for (const item of items) {
            if (item.id === id) {
                return item;
            }
            if (item.children) {
                const found = this.findMenuItemById(item.children, id);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    }

    // public updateMenuItem(updatedItem: FuseNavigationItem): void {
    //     const menuItem = this.getMenuItemById(updatedItem.id);
    //     if (menuItem) {
    //         // Mevcut menü öğesinin özelliklerini güncelle
    //         Object.assign(menuItem, updatedItem);
    //         // Navigasyon bileşenini yenile
    //         this.navComponent.refresh();
    //     } else {
    //         console.error(`Menu item with id ${updatedItem.id} not found.`);
    //     }
    // }

    public refreshMenu(): void {
        this.emitNavigationUpdate();
    }

    generateCustomReportMenuItem(report: CustomReportEditor.CustomReport): FuseNavigationItem {

        let link: string;
        switch (report.groupKey) {
            case 'Sensor':
                link = `/live-tag-properties/${report.id}`;
                break;
            case 'Location':
                link = `/live-tag-locations/${report.id}`;
                break;

            case 'SensorHistory':
                link = `/reports/tag-property/${report.id}`;
                break;
            case 'SensorAlertHistory':
                link = `/reports/tag-property-alert/${report.id}`;
                break;
            case 'LocationHistory':
                link = `/reports/tracking/${report.id}`;
                break;
            case 'ZoneEngagement':
                link = `/reports/zone-engagement/${report.id}`;
                break;
            case 'PresenceSummary':
                link = `/reports/presence-summary/${report.id}`;
                break;
            case 'AccessRuleDwell':
                link = `/reports/access-rule-dwell/${report.id}`;
                break;
            case 'ReaderPropertyHistory':
            case 'ReaderHealthHistory':
                link = `/reports/reader-property/${report.id}`;
                break;
            default:
                console.error('Unknown report groupKey:', report.groupKey);
                break;
        }

        let titleColor = "text-white";

        if (report?.colorCode)
            titleColor = this.styleManager.addColorClass(report?.colorCode)


        let wrapperColor = "text-white";
        if (report?.iconColorCode)
            wrapperColor = this.styleManager.addColorClass(report?.iconColorCode);

        const icon = this.utility.flatIcon(report.icon) ?? 'fal:file-chart-column';

        return {
            id: report.id.toString(), // Convert Guid to string
            title: report.name,
            icon: icon,
            badge: this.getCustomReportBadge(report),
            link: link,
            exactMatch: true,
            type: 'basic',
            classes: { wrapper: wrapperColor, title: titleColor },
            meta: { order: report.order }
        };

    }

    private getCustomReportBadge(report: CustomReportEditor.CustomReport): FuseNavigationItem['badge'] | null {
        if (this.isCustomReportNew(report)) {
            return {
                title: 'REPORTS.CUSTOM.NEW_BADGE',
                classes: [
                    'inline-flex items-center rounded-md bg-emerald-400/10 px-2 py-1 text-xs font-medium text-emerald-500 ring-1 ring-inset ring-emerald-400/20',
                    !report?.isPublic ? 'after:ml-1.5 after:text-yellow-500 after:content-["●"]' : ''
                ].filter(Boolean).join(' ')
            };
        }

        if (!report?.isPublic) {
            return {
                title: '●',
                classes: 'inline-flex items-center  px-2 py-1 text-xs font-medium text-yellow-500 '
            };
        }

        return null;
    }

    private isCustomReportNew(report: CustomReportEditor.CustomReport): boolean {
        const createdAt = this.asDate(report?.createdAt);
        if (!createdAt) {
            return false;
        }

        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return createdAt >= oneMonthAgo;
    }

    private asDate(value: Date | string | null | undefined): Date | null {
        if (!value) {
            return null;
        }

        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    getCustomReportMenuItem(id: string | Guid): CustomReportEditor.CustomReport {
        return this.customReports.find(report => report.id?.toString() === id?.toString());
    }

    addCustomReportMenuItem(reportModel: CustomReportEditor.CustomReport) {
        // Önce customReports array'ine ekle
        this.customReports.push(reportModel);

        // Menüyü yeniden oluştur
        this.refreshCustomReportsMenu().subscribe();
    }

    updateCustomReportMenuItem(report: CustomReportEditor.CustomReport) {
        // customReports array'ini güncelle
        const index = this.customReports.findIndex(r => r.id === report.id);
        if (index !== -1) {
            this.customReports[index] = report;
        }

        // Menüyü yeniden oluştur
        this.refreshCustomReportsMenu().subscribe();
    }

    removeCustomReportMenuItem(id: Guid) {
        // customReports array'inden kaldır
        this.customReports = this.customReports.filter(report => report.id !== id);

        // Menüyü yeniden oluştur
        this.refreshCustomReportsMenu().subscribe();
    }

    private filterVisibleMenuItems(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return (items || [])
            .map((item: any) => {
                const children = this.filterVisibleMenuItems(item.children || []);
                const canShowSelf = this.canShowMenuItem(item);

                if (!canShowSelf && children.length === 0) {
                    return null;
                }

                return {
                    ...item,
                    hidden: false,
                    children: children.length > 0 ? children : undefined,
                } as FuseNavigationItem;
            })
            .filter(Boolean) as FuseNavigationItem[];
    }

    private canShowMenuItem(menuItem: any): boolean {
        if (menuItem.hide === true) {
            return false;
        }

        if (this.isDashboardHomeItem(menuItem)) {
            return true;
        }

        if (!this.thirdPartyAppsFeature.isEnabled() && this.isThirdPartyAppsMenuItem(menuItem)) {
            return false;
        }

        const permissionTarget = menuItem.screenCode || menuItem.id;
        const canView = this.permissionService.canViewSync(permissionTarget);
        if (canView !== null) {
            return canView;
        }

        const roles = menuItem.roles || [];
        return roles.length === 0 || this._badisOauthLoginService.isUserInRoles(roles);
    }

    private isDashboardHomeItem(menuItem: any): boolean {
        const id = String(menuItem?.id ?? '').trim().toLocaleLowerCase();
        const link = String(menuItem?.link ?? '').trim().toLocaleLowerCase();
        const screenCode = String(menuItem?.screenCode ?? '').trim().toLocaleLowerCase();

        return id === 'dashboards-home'
            || screenCode === 'dashboards-home'
            || link === '/dashboards/home';
    }

    private isThirdPartyAppsMenuItem(menuItem: any): boolean {
        const id = String(menuItem?.id ?? '').trim().toLocaleLowerCase();
        const link = String(menuItem?.link ?? '').trim().toLocaleLowerCase();
        const screenCode = String(menuItem?.screenCode ?? '').trim().toLocaleLowerCase();
        const title = String(menuItem?.title ?? '').trim().toLocaleLowerCase();

        return id === 'third-party-apps'
            || id === 'third-party-app-store'
            || id === 'maintenances-app-store'
            || id === 'apps'
            || id === 'app-store'
            || id.startsWith('third-party-app-')
            || screenCode.startsWith('third-party-app')
            || screenCode === 'third-party-app-store'
            || title === 'apps'
            || title === 'app store'
            || title === 'uygulamalar'
            || title === 'uygulama mağazası'
            || title.startsWith('systemadmin.menu.apps')
            || link === '/apps'
            || link.startsWith('/apps/');
    }

    private cloneNavigation(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return JSON.parse(JSON.stringify(items || []));
    }

    private emitNavigationUpdate(): void {
        if (!this.newNavigation) {
            return;
        }

        const visibleNavigation = this.filterVisibleMenuItems(this.newNavigation);
        this.varService.navizardSystemMenuSource = this.cloneNavigation(this.newNavigation);
        this.varService.navizardSystemMenu = visibleNavigation;
        this._fuseNavigationService.storeNavigation('mainNavigation', visibleNavigation);
        this.navigationService.setMenu(this.newNavigation);
        this.refreshMainNavigationComponent();
    }

    private refreshMainNavigationComponent(): void {
        const refresh = () => this._fuseNavigationService
            .getComponent<any>('mainNavigation')
            ?.refresh?.();

        refresh();
        setTimeout(refresh);
    }

}
